import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Upload, Download, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { useToast } from "./hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Alert, AlertDescription } from "./ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

import { doc, writeBatch, collection } from "firebase/firestore";
import { db } from "../firebase";
import { v4 as uuidv4 } from "uuid";

// Helper function to merge hazard data
const mergeHazardData = (existingData = {}, newData) => {
  const merged = {
    hazardId: "",
    description: "",
    type: "",
    hazardSource: "",
    causes: [],
    consequences: [],
    riskCategory: [],
    unmitigatedRisk: {
      probability: "",
      severity: "",
      rating: "",
    },
    riskStatus: "Open",
    sfairpStatement: "",
    retainedRisk: {
      probability: "",
      severity: "",
      rating: "",
    },
    residualRiskDescription: "",
    mitigations: [],
    logs: [],
    systems: [],
    operatingModes: [],
    locations: [],
    projectLifecycles: [],
    disciplines: [],
    drawingPackages: [],
    users: [],
    meetings: [],
  };

  // If existing data exists, merge it
  if (existingData) {
    Object.keys(merged).forEach((key) => {
      if (Array.isArray(merged[key])) {
        merged[key] = existingData[key] || [];
      } else if (typeof merged[key] === "object") {
        // Ensure we have a valid object to merge with
        if (existingData[key] && typeof existingData[key] === "object") {
          merged[key] = { ...merged[key], ...existingData[key] };
        }
        // If not, keep the default structure we defined above
      } else {
        merged[key] = existingData[key] || merged[key];
      }
    });
  }

  // Merge new data if it exists
  if (newData) {
    if (newData.Description !== undefined) {
      merged.description = newData.Description || "";
    }

    if (newData["Risk Category"] !== undefined) {
      merged.riskCategory = Array.isArray(newData["Risk Category"])
        ? newData["Risk Category"]
        : String(newData["Risk Category"] || "").split(",")
            .map((cat) => cat.trim())
            .filter(Boolean);
    }

    // Ensure we have a risk status (default to "Open" if empty or undefined)
    if (newData["Risk Status"] !== undefined) {
      merged.riskStatus = newData["Risk Status"] || "Open";
    }

    if (newData["SFAIRP Statement"] !== undefined) {
      merged.sfairpStatement = newData["SFAIRP Statement"] || "";
    }

    if (newData["Hazard Type"] !== undefined) {
      merged.type = newData["Hazard Type"] || "";
    }

    // Ensure hazard source is properly handled (empty string is acceptable)
    if (newData["Hazard Source"] !== undefined) {
      merged.hazardSource = newData["Hazard Source"] || "";
    }

    if (newData["Meeting"] !== undefined && newData["Meeting"] !== null) {
      merged.meetings = String(newData["Meeting"] || "").split(",")
        .map(item => item.trim())
        .filter(Boolean);
    }

    // Handle array properties with better empty value handling
    [
      "Causes",
      "Consequences",
      "Systems",
      "Operating Modes",
      "Locations",
      "Project Lifecycles",
      "Disciplines",
      "Drawing Packages",
      "Users",
    ].forEach((field) => {
      const key = field.toLowerCase().replace(/\s+/g, "");

      if (newData[field] !== undefined && newData[field] !== null) {
        merged[key] = Array.isArray(newData[field])
          ? newData[field].filter(Boolean)
          : String(newData[field] || "").split(",")
              .map((item) => item.trim())
              .filter(Boolean);
      }
    });

    // Handle risk ratings with better error handling
    ["Unmitigated Risk", "Retained Risk"].forEach((riskType) => {
      const key = riskType.toLowerCase().replace(/\s+/g, "");
      
      // Initialize the risk object if it doesn't exist
      if (!merged[key]) {
        merged[key] = { probability: "", severity: "", rating: "" };
      }

      // Safely update probability if available
      if (newData[`${riskType} Probability`] !== undefined) {
        merged[key].probability = newData[`${riskType} Probability`] || "";
      }
      
      // Safely update severity if available
      if (newData[`${riskType} Severity`] !== undefined) {
        merged[key].severity = newData[`${riskType} Severity`] || "";
      }
      
      // Safely update rating if available
      if (newData[`${riskType} Rating`] !== undefined) {
        merged[key].rating = newData[`${riskType} Rating`] || "";
      }
    });

    // Handle residual risk description
    if (newData["Residual Risk Description"] !== undefined) {
      merged.residualRiskDescription = newData["Residual Risk Description"] || "";
    }

    // Preserve the Hazard ID if it exists in the newData
    if (newData["Hazard ID"] !== undefined) {
      merged.hazardId = newData["Hazard ID"] || "";
    }
  }

  return merged;
};

// Helper function to process mitigation data
const processMitigation = (mitigation) => {
  if (!mitigation["Description"] && !mitigation["Mitigation ID"]) {
    return null;
  }

  return {
    id: mitigation["_internal_id"] || uuidv4(), // Internal ID for React keys
    mitigationId: mitigation["Mitigation ID"] || "", // Preserve the formatted mitigation ID
    type: mitigation["Type"] || "Engineering",
    description: mitigation["Description"] || "",
    owner: mitigation["Owner"] || "",
    status: mitigation["Status"] || "Proposed",
    dueDate: mitigation["Due Date"] || "",
    safetyRequirement: mitigation["Safety Requirement"] || "",
    evidence: mitigation["Evidence"] || "",
  };
};

// Create sample Excel template
const createExcelTemplate = () => {
  const wb = XLSX.utils.book_new();
  
  // Hazards sheet template with two examples: new hazard and update existing hazard
  const hazardTemplateData = [
    {
      "Hazard ID": "",  // Leave empty for new hazard - system will auto-generate ID
      "Description": "NEW HAZARD: This is an example of creating a new hazard",
      "Hazard Type": "Mechanical",
      "Hazard Source": "Equipment Failure",
      "Causes": "1. Cause 1\n2. Cause 2",
      "Consequences": "1. Consequence 1\n2. Consequence 2",
      "Risk Category": "Safety, Environmental",
      "Systems": "System 1, System 2",
      "Operating Modes": "Normal, Emergency",
      "Locations": "Location 1, Location 2",
      "Project Lifecycles": "Design, Construction",
      "Disciplines": "Mechanical, Electrical",
      "Drawing Packages": "Package A, Package B",
      "Users": "Operators, Maintenance",
      "Meeting": "Safety Review",
      "Unmitigated Risk Probability": "Probable",
      "Unmitigated Risk Severity": "Critical",
      "Unmitigated Risk Rating": "High",
      "Retained Risk Probability": "Remote",
      "Retained Risk Severity": "Marginal",
      "Retained Risk Rating": "Low",
      "SFAIRP Statement": "Risk has been reduced to ALARP",
      "Risk Status": "Open",
      "Residual Risk Description": "Minimal residual risk"
    },
    {
      "Hazard ID": "PROJ-HAZ-0001",  // Enter existing Hazard ID to update it
      "Description": "EXISTING HAZARD: This will update an existing hazard with ID PROJ-HAZ-0001",
      "Hazard Type": "Electrical",
      "Hazard Source": "Exposed Wiring",
      "Causes": "1. Poor installation\n2. Damaged insulation",
      "Consequences": "1. Electrical shock\n2. Fire hazard",
      "Risk Category": "Safety, Performance",
      "Systems": "Electrical System, Control Panel",
      "Operating Modes": "All modes",
      "Locations": "Control Room",
      "Project Lifecycles": "Operation, Maintenance",
      "Disciplines": "Electrical",
      "Drawing Packages": "Electrical Schematics",
      "Users": "Maintenance Staff",
      "Meeting": "Safety Committee",
      "Unmitigated Risk Probability": "Occasional",
      "Unmitigated Risk Severity": "Critical",
      "Unmitigated Risk Rating": "Serious",
      "Retained Risk Probability": "Improbable",
      "Retained Risk Severity": "Marginal",
      "Retained Risk Rating": "Minor",
      "SFAIRP Statement": "Isolation procedures implemented",
      "Risk Status": "Managed",
      "Residual Risk Description": "Minimal risk with proper procedures"
    }
  ];
  
  // Mitigations sheet template with examples for both hazards
  const mitigationTemplateData = [
    {
      "Hazard ID": "",  // Leave empty to match the new hazard (will be auto-generated)
      "Hazard Description": "NEW HAZARD: This is an example of creating a new hazard",
      "Mitigation ID": "",  // Leave empty for new mitigation - system will auto-generate ID
      "Type": "Engineering",
      "Description": "Install safety guards on moving parts",
      "Owner": "Safety Engineer",
      "Status": "Proposed",
      "Due Date": "2025-12-31",
      "Safety Requirement": "All moving parts must be guarded",
      "Evidence": "http://evidence-link.com"
    },
    {
      "Hazard ID": "",  // Leave empty for new hazard's second mitigation
      "Hazard Description": "NEW HAZARD: This is an example of creating a new hazard",
      "Mitigation ID": "",  // Leave empty for new mitigation
      "Type": "Administrative",
      "Description": "Implement training program for operators",
      "Owner": "Training Department",
      "Status": "Proposed",
      "Due Date": "2025-11-15",
      "Safety Requirement": "All operators must complete safety training",
      "Evidence": ""
    },
    {
      "Hazard ID": "PROJ-HAZ-0001",  // Use existing Hazard ID for existing hazard's mitigation
      "Hazard Description": "EXISTING HAZARD: This will update an existing hazard",
      "Mitigation ID": "PROJ-MIT-0001",  // Use existing Mitigation ID to update it
      "Type": "Engineering",
      "Description": "Install insulation covers on exposed wiring",
      "Owner": "Electrical Engineer",
      "Status": "In Progress",
      "Due Date": "2025-06-30",
      "Safety Requirement": "All exposed wiring must be covered",
      "Evidence": "http://photos-of-installation.com"
    },
    {
      "Hazard ID": "PROJ-HAZ-0001",  // Use existing Hazard ID 
      "Hazard Description": "EXISTING HAZARD: This will update an existing hazard",
      "Mitigation ID": "",  // Leave empty to add a NEW mitigation to an existing hazard
      "Type": "Administrative",
      "Description": "Update lockout/tagout procedures",
      "Owner": "Safety Officer",
      "Status": "Proposed",
      "Due Date": "2025-07-15",
      "Safety Requirement": "Updated procedures must be followed",
      "Evidence": ""
    }
  ];
  
  const hazardsWS = XLSX.utils.json_to_sheet(hazardTemplateData);
  const mitigationsWS = XLSX.utils.json_to_sheet(mitigationTemplateData);
  
  XLSX.utils.book_append_sheet(wb, hazardsWS, "Hazards");
  XLSX.utils.book_append_sheet(wb, mitigationsWS, "Mitigations");
  
  // Set column widths
  hazardsWS["!cols"] = Array(Object.keys(hazardTemplateData[0]).length).fill({ wch: 25 });
  mitigationsWS["!cols"] = Array(Object.keys(mitigationTemplateData[0]).length).fill({ wch: 25 });
  
  return wb;
};

export const HazardExcelImport = ({ project, hazards, onImportComplete }) => {
  const [importing, setImporting] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [importDetails, setImportDetails] = useState(null);
  const [importSheetOption, setImportSheetOption] = useState("both");
  
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const downloadTemplate = () => {
    try {
      const wb = createExcelTemplate();
      XLSX.writeFile(wb, "Hazard_Import_Template.xlsx");
      
      toast({
        title: "Template downloaded",
        description: "The Excel template has been downloaded to your device",
      });
    } catch (error) {
      console.error("Template download error:", error);
      
      toast({
        variant: "destructive",
        title: "Download failed",
        description: "Failed to generate template file",
      });
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      
      // Check if required sheets exist
      const hasHazardsSheet = workbook.SheetNames.includes("Hazards");
      const hasMitigationsSheet = workbook.SheetNames.includes("Mitigations");
      
      if (!hasHazardsSheet && !hasMitigationsSheet) {
        throw new Error("Excel file must contain 'Hazards' and/or 'Mitigations' sheets");
      }

      // Process hazards sheet
      const hazardData = hasHazardsSheet ? 
        XLSX.utils.sheet_to_json(workbook.Sheets["Hazards"]) : [];
        
      // Process mitigations sheet
      const mitigationData = hasMitigationsSheet ?
        XLSX.utils.sheet_to_json(workbook.Sheets["Mitigations"]) : [];
      
      // Prepare import details
      const details = {
        hazardsCount: hazardData.length,
        mitigationsCount: mitigationData.length,
        hazardData,
        mitigationData,
      };
      
      setImportDetails(details);
      setIsTemplateDialogOpen(true);
    } catch (error) {
      console.error("Import preparation error:", error);
      
      toast({
        variant: "destructive",
        title: "Import preparation failed",
        description: error.message,
      });
    } finally {
      setImporting(false);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };
  
  const processImport = async () => {
    if (!importDetails || !project || !project.id) {
      toast({
        variant: "destructive",
        title: "Import failed",
        description: "Missing project information or import data",
      });
      return;
    }
    
    setImporting(true);
    
    try {
      // Process based on user selection
      const { hazardData, mitigationData } = importDetails;
      let hazardGroups = {};
      
      // Process hazard data if selected
      if (importSheetOption === "hazards" || importSheetOption === "both") {
        hazardData.forEach((row) => {
          // Check for Firestore ID first (from previously exported data)
          const firestoreId = row["_firestoreId"];
          const hazardId = row["Hazard ID"];
          
          // Generate a temporary ID for rows without any ID
          const groupKey = firestoreId || hazardId || `new-hazard-${uuidv4()}`;
          
          // If we have a Firestore ID, look up by that first
          let existingHazard = null;
          if (firestoreId) {
            existingHazard = hazards.find(h => h.id === firestoreId);
          }
          
          // If not found by Firestore ID, try by hazard ID
          if (!existingHazard && hazardId) {
            existingHazard = hazards.find(h => h.hazardId === hazardId);
          }
          
          // Make sure we have at least a description before importing
          if (!row.Description && !existingHazard) {
            console.warn("Skipping row without description:", row);
            return; // Skip rows without description
          }
          
          // Initialize the hazard group with base hazard data
          hazardGroups[groupKey] = {
            data: mergeHazardData(existingHazard, row),
            mitigations: [], // Will store all mitigations for this hazard
            isNew: !existingHazard,
            firestoreId: existingHazard?.id,
            hazardId: hazardId
          };
        });
      }
      
      // Process mitigations if selected
      if (importSheetOption === "mitigations" || importSheetOption === "both") {
        mitigationData.forEach((row) => {
          // Try to find hazard using various IDs
          const hazardFirestoreId = row["_hazard_firestoreId"];
          const hazardId = row["Hazard ID"];
          
          if (!hazardId && !hazardFirestoreId) return; // Skip rows without hazard ID
          
          let existingHazard = null;
          let groupKey = null;
          
          // Try to find hazard by Firestore ID first
          if (hazardFirestoreId) {
            existingHazard = hazards.find(h => h.id === hazardFirestoreId);
            if (existingHazard) {
              groupKey = existingHazard.id;
            }
          }
          
          // If not found, try by hazard ID
          if (!existingHazard && hazardId) {
            existingHazard = hazards.find(h => h.hazardId === hazardId);
            if (existingHazard) {
              groupKey = existingHazard.id;
            } else {
              groupKey = hazardId; // Use hazard ID as key if not found
            }
          }
          
          // Find existing group or create one
          let existingGroup = groupKey ? hazardGroups[groupKey] : null;
          
          // If we don't have this hazard yet, create an entry for it
          if (!existingGroup && existingHazard) {
            hazardGroups[existingHazard.id] = {
              data: { ...existingHazard },
              mitigations: [],
              isNew: false,
              firestoreId: existingHazard.id,
              hazardId: existingHazard.hazardId
            };
            existingGroup = hazardGroups[existingHazard.id];
          } else if (!existingGroup && !existingHazard && hazardId) {
            // Can't add mitigation to non-existent hazard - warn but don't fail
            console.warn(`Skipping mitigation for unknown hazard: ${hazardId}`);
            return;
          }
          
          // Process mitigation and add to the appropriate hazard
          const mitigation = processMitigation(row);
          if (mitigation && existingGroup) {
            existingGroup.mitigations.push(mitigation);
          }
        });
      }

      // Now directly use Firestore batch operations instead of hazardService
      const batch = writeBatch(db);
      const projectRef = doc(db, `projects/${project.id}`);
      let hazardCounter = project.hazardCounter || 1;
      let mitigationCounter = project.mitigationCounter || 1;

      // Process each hazard group
      const processedHazards = Object.values(hazardGroups).map(
        ({ data, mitigations, isNew, firestoreId, hazardId }) => {
          try {
            // Skip if we have no data to process
            if (!data || (!isNew && !mitigations.length)) {
              return null;
            }
            
            const existingMitigations = isNew ? [] : data.mitigations || [];
            
            const mitigationResults = {
              created: [],
              updated: [],
            };
            
            // Process mitigations and assign IDs if needed
            mitigations.forEach((newMit) => {
              // Check if this is a new mitigation or an existing one
              const existingMitigation = existingMitigations.find(
                (existingMit) => existingMit.id === newMit.id || existingMit.mitigationId === newMit.mitigationId
              );
              
              if (existingMitigation) {
                // Update existing mitigation
                mitigationResults.updated.push(newMit);
              } else {
                // Create new mitigation with ID
                if (!newMit.mitigationId) {
                  newMit.mitigationId = `${project.acronym}-MIT-${padNumber(mitigationCounter++, 4)}`;
                }
                mitigationResults.created.push(newMit);
              }
            });
            
            // Combine existing and new mitigations
            const combinedMitigations = isNew
              ? mitigations
              : [
                  // Keep existing mitigations that aren't being updated
                  ...existingMitigations.filter(
                    (existingMit) => !mitigations.some(
                      (newMit) => newMit.id === existingMit.id || newMit.mitigationId === existingMit.mitigationId
                    )
                  ),
                  ...mitigations, // Add all new/updated mitigations
                ];

            // Prepare the final hazard data
            const hazardData = {
              ...data,
              mitigations: combinedMitigations,
            };

            // If new hazard, assign hazard ID if needed
            if (isNew) {
              // Check for placeholder ID values like "NEW", "TBD", etc. to replace them with generated IDs
              const placeholderValues = ["NEW", "TBD", "PLACEHOLDER", "AUTO", ""];
              if (!hazardData.hazardId || placeholderValues.includes(hazardData.hazardId.trim().toUpperCase())) {
                hazardData.hazardId = `${project.acronym}-HAZ-${padNumber(hazardCounter++, 4)}`;
              }
              
              // Create new hazard in Firestore
              const newHazardRef = doc(collection(db, `projects/${project.id}/hazards`));
              batch.set(newHazardRef, hazardData);
              
              return {
                ...hazardData,
                id: newHazardRef.id,
                type: "created",
                mitigationResults,
              };
            } else if (firestoreId) {
              // Update existing hazard
              const hazardRef = doc(db, `projects/${project.id}/hazards/${firestoreId}`);
              batch.update(hazardRef, hazardData);
              
              return {
                ...hazardData,
                id: firestoreId,
                type: "updated",
                mitigationResults,
              };
            }
            
            return null;
          } catch (error) {
            console.error(`Error processing hazard:`, error);
            throw error;
          }
        }
      ).filter(Boolean);

      // Update counters in the project document
      batch.update(projectRef, { 
        hazardCounter, 
        mitigationCounter
      });

      // Commit all the changes in one batch
      await batch.commit();
      
      // Calculate results for toast notification
      const created = processedHazards.filter((h) => h?.type === "created").length;
      const updated = processedHazards.filter((h) => h?.type === "updated").length;
      
      // Count created and updated mitigations
      const mitigationStats = processedHazards.reduce(
        (stats, hazard) => {
          if (hazard?.mitigationResults) {
            stats.created += hazard.mitigationResults.created.length;
            stats.updated += hazard.mitigationResults.updated.length;
          }
          return stats;
        },
        { created: 0, updated: 0 }
      );
      
      toast({
        title: "Import successful",
        description:
          `Hazards: ${created} created, ${updated} updated\n` +
          `Mitigations: ${mitigationStats.created} created, ${mitigationStats.updated} updated`,
      });
      
      if (onImportComplete) {
        onImportComplete();
      }
      
      // Close dialog
      setIsTemplateDialogOpen(false);
      setImportDetails(null);
    } catch (error) {
      console.error("Import error:", error);
      
      toast({
        variant: "destructive",
        title: "Import failed",
        description: error.message,
      });
    } finally {
      setImporting(false);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Helper function for padding numbers
  function padNumber(number, size) {
    var s = "0000" + number;
    return s.substring(s.length - size);
  }

  return (
    <>
      <div className="flex space-x-2">
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={importing}
          className="flex items-center"
        >
          {importing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <span>Importing...</span>
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              <span>Import from Excel</span>
            </>
          )}
        </Button>

        <Button
          variant="secondary"
          onClick={downloadTemplate}
          className="flex items-center"
        >
          <Download className="mr-2 h-4 w-4" />
          <span>Download Template</span>
        </Button>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImport}
          accept=".xlsx,.xls"
          className="hidden"
        />
      </div>

      <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Import Hazards and Mitigations</DialogTitle>
            <DialogDescription>
              Review the data to be imported from your Excel file.
            </DialogDescription>
          </DialogHeader>
          
          {importDetails && (
            <div className="py-4">
              <Alert className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Found {importDetails.hazardsCount} hazards and {importDetails.mitigationsCount} mitigations in the Excel file.
                </AlertDescription>
              </Alert>
              
              <Tabs defaultValue="both" onValueChange={setImportSheetOption}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="both">Import All</TabsTrigger>
                  <TabsTrigger value="hazards">Hazards Only</TabsTrigger>
                  <TabsTrigger value="mitigations">Mitigations Only</TabsTrigger>
                </TabsList>
                <TabsContent value="both" className="pt-2">
                  Import both hazards and mitigations from the Excel file
                </TabsContent>
                <TabsContent value="hazards" className="pt-2">
                  Import only hazards (no mitigations) from the Excel file
                </TabsContent>
                <TabsContent value="mitigations" className="pt-2">
                  Import only mitigations for existing hazards
                </TabsContent>
              </Tabs>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTemplateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={processImport} disabled={importing}>
              {importing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                  Processing...
                </>
              ) : (
                'Import'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HazardExcelImport;